import { APIClient } from "@lib/api";
import { isSupportedMobileBrowser } from "@lib/browser";
import { to } from "@lib/utils";
import { AuthParam } from "@models/auth";
import { AuthError } from "@models/error";
import { Network } from "@models/network";
import { User, UserParam } from "@models/user";
import { WalletProvider } from "@models/wallet";
import * as Sentry from "@sentry/browser";
import {
  GoogleAuthProvider,
  sendSignInLinkToEmail,
  signInWithRedirect,
  signOut,
} from "firebase/auth";
import { isEmpty } from "lodash";
import { SiweMessage } from "siwe";
import { create } from "zustand";
import { firebaseAuth } from "./firebase";
import { getParticleWallet, initParticleNetwork } from "./particle";

const API_HOST = process.env.NEXT_PUBLIC_API_HOST || "";
if (!API_HOST) throw "Missing NEXT_PUBLIC_API_HOST";

const API_KEY = process.env.NEXT_PUBLIC_API_KEY || "";
if (!API_KEY) throw "Missing NEXT_PUBLIC_API_KEY";

export const apiClient = new APIClient(API_HOST, API_KEY);

export const STORAGE_AUTH_EMAIL_KEY = "moongate-auth-email";
export const STORAGE_AUTH_TOKEN_KEY = "moongate-auth-token";

const GOOGLE_AUTH_PROVIDER = new GoogleAuthProvider();

export enum OAuthProvider {
  GOOGLE = "google",
}

interface UserStore {
  user: User | null;
  initialized: boolean;
  authenticating: boolean;
  fetching: boolean;
  idToken: string; // firebase idToken
  authToken: string;
  sentEmailLink: boolean;
  firebaseAuthenticating: boolean;
  creatingWallet: boolean;
  supportedBrowser: boolean;
  enableParticle: boolean;
  enableWalletLogin: boolean;
}

export const useUserStore = create<UserStore>((set) => ({
  user: null,
  initialized: false,
  authenticating: false,
  fetching: false,
  idToken: "", // firebase idToken
  authToken: "",
  sentEmailLink: false,
  firebaseAuthenticating: false,
  creatingWallet: false,
  supportedBrowser: true,
  enableParticle: true,
  enableWalletLogin: false,
}));

export const sendSignInEmail = async (email: string) => {
  Sentry.addBreadcrumb({
    category: "sendSignInEmail",
    level: "info",
  });
  Sentry.setUser({
    email,
  });
  const actionCodeSettings = {
    url: `${window.location.origin}${window.location.pathname}${
      window.location.search || "?"
    }&email=${encodeURIComponent(email)}`,
    handleCodeInApp: true,
  };
  console.log("sendSignInEmail", actionCodeSettings);
  const { err } = await to(
    sendSignInLinkToEmail(firebaseAuth, email, actionCodeSettings)
  );
  if (err) {
    console.error(err.code, err.message);
    throw err.code;
  }
  useUserStore.setState({ sentEmailLink: true });
  window.localStorage.setItem(STORAGE_AUTH_EMAIL_KEY, email);
  return true;
};

export const signInWithGoogle = () => {
  Sentry.addBreadcrumb({
    category: "signInWithGoogle",
    level: "info",
  });
  return signInWithRedirect(firebaseAuth, GOOGLE_AUTH_PROVIDER);
};

const setAuthStates = (user: User | null, authToken: string) => {
  useUserStore.setState({
    user,
    authToken,
    fetching: false,
    authenticating: false,
    creatingWallet: false,
  });
  if (authToken)
    window.localStorage.setItem(
      STORAGE_AUTH_TOKEN_KEY,
      JSON.stringify(authToken)
    );
  apiClient.session = user?.session || "";
  apiClient.authId = user?.id || "";
  apiClient.authToken = authToken;
};

export const signIn = async (
  authId: string,
  authIdToken: string,
  email: string
) => {
  if (useUserStore.getState().fetching || useUserStore.getState().sentEmailLink)
    return null;
  Sentry.addBreadcrumb({
    category: "signIn",
    level: "info",
  });
  Sentry.setUser({
    authId,
    email,
  });
  useUserStore.setState({ fetching: true });
  console.log("signIn", authId, email);

  let user: User | null = null;
  let authToken = "";
  try {
    user = await checkExistingUser(authId, authIdToken, email);
    console.log("checkExistingUser", user);
    if (
      useUserStore.getState().enableParticle &&
      (!user || isEmpty(user.evmWallets))
    ) {
      Sentry.addBreadcrumb({
        category: "createUserWallet",
        level: "info",
      });
      console.log("signIn", "creating new wallet...");
      useUserStore.setState({ creatingWallet: true });
      if (!isSupportedMobileBrowser()) {
        Sentry.captureMessage(AuthError.UNSUPPORTED_BROWSER);
        useUserStore.setState({ supportedBrowser: false });
        return null;
      }
      const { err: walletErr, res: newWallet } = await to(
        getParticleWallet(authIdToken),
        0
      );
      if (!newWallet) {
        console.error(walletErr);
        throw AuthError.CREATE_WALLET_FAILED;
      }

      Sentry.setTag("wallet", newWallet);
      ({ user, authToken } = await fetchUserData({
        authId,
        authIdToken,
        email,
        address: newWallet,
        network: Network.EVM,
        provider: WalletProvider.PARTICLE_NETWORK,
        source: window.location.origin,
      }));
      if (!user) throw AuthError.SAVE_WALLET_FAILED;
    } else {
      ({ user, authToken } = await fetchUserData({
        authId,
        authIdToken,
        email,
      }));
      if (!user) throw AuthError.RETRIEVE_USER_DATA_FAILED;
    }
  } catch (error) {
    console.error("signIn", error);
    user = null;
  }
  setAuthStates(user, authToken);
  return user;
};

const checkExistingUser = async (
  authId: string,
  authIdToken: string,
  email: string
) => {
  Sentry.addBreadcrumb({
    category: "checkExistingUser",
    level: "info",
  });
  const data = await apiClient.get("/users/check", {
    email,
    authId,
    authIdToken,
  });
  if (!data?.id || !data?.identifiers) return null;
  const user = new User(data as UserParam);
  return user;
};

const fetchUserData = async (
  params: AuthParam
): Promise<{ user: User | null; authToken: string }> => {
  Sentry.addBreadcrumb({
    category: "fetchUserData",
    level: "info",
  });
  const data = await apiClient.post("/users/auth", params);
  if (!data || !data.user || !data.authToken)
    return { user: null, authToken: "" };

  const user = new User(data.user as UserParam);
  console.log("fetchUserData", user);
  const authToken = String(data.authToken);
  Sentry.setUser({
    id: user.id,
    authId: params.authId,
    email: params.email,
    address: params.address,
    network: params.network,
  });
  return { user, authToken };
};

export const signInByAuthToken = async (token: string) => {
  Sentry.addBreadcrumb({
    category: "signInByAuthToken",
    level: "info",
  });
  if (!token) return null;
  console.log("signInByAuthToken", token);
  try {
    useUserStore.setState({
      authenticating: true,
    });
    console.log("signInByAuthToken", token);
    const data = await apiClient.get(`/users/auth`, {
      token,
    });
    if (!data || !data.user || !data.authToken)
      throw AuthError.INVALID_AUTH_TOKEN;
    const { user: UserParam, authToken } = data;
    const user = new User(UserParam as UserParam);
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
    console.log("signInByAuthToken", authToken, user);
    setAuthStates(user, authToken);
    return user;
  } catch (error) {
    console.error("signInByAuthToken", error);
    logOut();
    return null;
  }
};

export const signInByWallet = async (
  address: string,
  siweMessage: SiweMessage,
  signature: string
) => {
  Sentry.addBreadcrumb({
    category: "signInByWallet",
    level: "info",
  });
  Sentry.setTag("wallet", address);
  console.log("signInByWallet", siweMessage);
  const { user, authToken } = await fetchUserData({
    address,
    siweMessage,
    signature,
  });
  Sentry.setUser({
    id: user?.id,
    email: user?.email,
    wallet: address,
  });
  setAuthStates(user, authToken);
  return user;
};

export const logOut = async (redirect = "") => {
  window.localStorage.removeItem(STORAGE_AUTH_TOKEN_KEY);
  apiClient.session = "";
  apiClient.authId = "";
  apiClient.authToken = "";
  if (useUserStore.getState().enableParticle) {
    const pn = initParticleNetwork();
    console.log(pn.auth.isLogin(), firebaseAuth.currentUser);
    if (pn.auth.isLogin()) await to(pn.auth.logout(true));
  }
  if (firebaseAuth.currentUser) await to(signOut(firebaseAuth));
  if (redirect) {
    window.location.href = redirect;
    return;
  }
  window.location.reload();
};
